import userService from '@/services/account/userService';

/**
 * @typedef {Object} ActiveNotifications
 * @property {boolean} conversationSentimentChanged - Whether the conversation sentiment changed
 * @property {boolean} conversationWindowCreated - Whether the conversation window was created
 * @property {boolean} conversationAgentChanged - Whether the conversation agent changed
 */

/**
 * @typedef {Object} NotificationsConfig
 * @property {string[]} pushNotificationIds - The user's push notification IDs
 * @property {ActiveNotifications} activeNotifications - The user's active notifications
 */

/**
 * @typedef {Object} User
 * @property {string} uuid - The user's UUID
 * @property {string} external_id - The user's external ID
 * @property {string} email - The user's email
 * @property {string} username - The user's username
 * @property {string} account_id - The user's account ID
 * @property {string} status - The user's status (ENABLED)
 * @property {string} language - The user's language
 * @property {string} contact_phone - The user's contact phone
 * @property {string} capabilities - The user's capabilities
 * @property {string} role - The user's role
 * @property {string[]} availability - The user's availability
 * @property {string[]} operational_region - The user's operational regions
 * @property {string} permission - The user's permission (ADMIN)
 * @property {number} date_created - The user's date created
 * @property {string} profile_picture - The user's profile picture URL
 * @property {NotificationsConfig} notifications_config - The user's notifications config
 * @property {boolean} invited - Whether the user is invited
 */

/**
 * @typedef {Object} Sort
 * @property {boolean} empty - Whether the sort is empty
 * @property {boolean} sorted - Whether the sort is sorted
 * @property {boolean} unsorted - Whether the sort is unsorted
 */

/**
 * @typedef {Object} Pageable
 * @property {number} offset - The offset of the page
 * @property {Sort} sort - The sort information
 * @property {number} pageNumber - The page number
 * @property {number} pageSize - The size of the page
 * @property {boolean} paged - Whether the result is paged
 * @property {boolean} unpaged - Whether the result is unpaged
 */

/**
 * @typedef {Object} PaginatedResponse<T>
 * @property {T[]} content - The list of items
 * @property {number} totalElements - The total number of elements
 * @property {number} totalPages - The total number of pages
 * @property {number} size - The size of the page
 * @property {number} number - The current page number
 * @property {Sort} sort - The sort information
 * @property {boolean} first - Whether this is the first page
 * @property {boolean} last - Whether this is the last page
 * @property {Pageable} pageable - The pageable information
 * @property {number} numberOfElements - The number of elements in this page
 * @property {boolean} empty - Whether the result is empty
 */

/**
 * @typedef {Object} AccountMemberState
 * @property {PaginatedResponse<User>} accountMembers - The list of account members
 * @property {boolean} loading - Whether the account members are loading
 * @property {string} error - The error message
 * @property {boolean} initialized - Whether the account members are initialized
 * @property {Object} pagination - The pagination information
 */

/**
 * @typedef {Object} vuexContext
 * @property {Function} dispatch - The dispatch function
 * @property {Function} commit - The commit function
 * @property {UserState} state - The state object
 */

/**
 * @type {AccountMemberState} The initial state of the account members module
 */
const state = {
    accountMembers: {},
    loading: false,
    error: null,
    initialized: false,
    pagination: {
        currentPage: 1,
        perPage: 10,
        total: 0,
    },
};

const getters = {
    /**
     * Get the list of account members
     * @param {AccountMemberState} state
     * @returns {PaginatedResponse<User>} The list of account members
     */
    accountMembers: (state) => state.accountMembers,

    /**
     * Get an account member by ID
     * @param {AccountMemberState} state
     * @param {string} id
     * @returns {User} The account member with the given ID
     */
    getMemberById: (state) => (id) => {
        if (!state.accountMembers || !state.accountMembers.content) return null;
        return state.accountMembers.content.find((member) => member.uuid === id);
    },

    /**
     * Get the loading status
     * @param {AccountMemberState} state
     * @returns {boolean} The loading status
     */
    loading: (state) => state.loading,

    /**
     * Get the error status
     * @param {AccountMemberState} state
     * @returns {string} The error status
     */
    error: (state) => state.error,

    /**
     * Get the initialized status
     * @param {AccountMemberState} state
     * @returns {boolean} The initialized status
     */
    initialized: (state) => state.initialized,

    pagination: (state) => state.pagination,
};

const mutations = {
    SET_ACCOUNT_MEMBERS(state, members) {
        state.accountMembers = members;
    },
    SET_LOADING(state, loading) {
        state.loading = loading;
    },
    SET_ERROR(state, error) {
        state.error = error;
    },
    SET_INITIALIZED(state, initialized) {
        state.initialized = initialized;
    },
    ADD_MEMBER(state, member) {
        state.accountMembers.content.push(member);
    },
    UPDATE_MEMBER(state, member) {
        // Los miembros viven en .content (la respuesta paginada del backend); buscarlos
        // en el objeto que la envuelve no encontraba nunca el índice, así que la tabla
        // no reflejaba ninguna edición hasta recargar.
        const index = state.accountMembers.content.findIndex((m) => m.uuid === member.uuid);
        if (index !== -1) {
            state.accountMembers.content.splice(index, 1, member);
        }
    },
    REMOVE_MEMBER(state, memberId) {
        state.accountMembers.content = state.accountMembers.content.filter((m) => m.uuid !== memberId);
    },
    SET_PAGINATION(state, pagination) {
        state.pagination = { ...state.pagination, ...pagination };
    },
};

const actions = {
    /**
     * Fetch the account members
     * @param {vuexContext} context
     * @param {number} page - The page number (0-based)
     * @param {number} size - The page size
     */
    async fetchAccountMembers({ commit }, { page = 0, size = 10 } = {}) {
        commit('SET_LOADING', true);
        try {
            const paginatedMembersResponse = await userService.fetchUsers(page, size);
            /** @type {PaginatedResponse<User>} */
            const paginatedMembers = paginatedMembersResponse.data;
            commit('SET_ACCOUNT_MEMBERS', paginatedMembers);
            commit('SET_PAGINATION', {
                currentPage: page + 1,
                perPage: size,
                total: paginatedMembers.totalElements,
            });
            commit('SET_ERROR', null);
            commit('SET_INITIALIZED', true);
        } catch (error) {
            console.error('Error fetching account members:', error);
            commit('SET_ERROR', error);
        } finally {
            commit('SET_LOADING', false);
        }
    },

    /**
     * Update a user
     * @param {vuexContext} context
     * @param {Object} userData - The user data to update
     */
    async updateUser({ commit }, { userId, userData }) {
        commit('SET_LOADING', true);
        try {
            const updatedUser = await userService.editUser(userId, userData);
            commit('UPDATE_MEMBER', updatedUser);
            commit('SET_ERROR', null);
        } catch (error) {
            console.error('Error updating user:', error);
            commit('SET_ERROR', error);
            throw error;
        } finally {
            commit('SET_LOADING', false);
        }
    },

    /**
     * Delete a user
     * @param {vuexContext} context
     * @param {string} userId - The ID of the user to delete
     */
    async deleteUser({ commit }, userId) {
        commit('SET_LOADING', true);
        try {
            await userService.deleteUser(userId);
            commit('REMOVE_MEMBER', userId);
            commit('SET_ERROR', null);
        } catch (error) {
            console.error('Error deleting user:', error);
            commit('SET_ERROR', error);
            throw error;
        } finally {
            commit('SET_LOADING', false);
        }
    },
};

/**
 * @typedef {Object} AccountMemberModule
 * @property {boolean} namespaced - Whether the module is namespaced
 * @property {AccountMemberState} state - The state of the module
 * @property {Object} getters - The getters of the module
 * @property {Object} actions - The actions of the module
 * @property {Object} mutations - The mutations of the module
 */

/**
 * @type {AccountMemberModule} The Account Member module
 */
export default {
    namespaced: true,
    state,
    getters,
    mutations,
    actions,
};
